import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './YoutubePlay.css';
import PlaylistOverlay from './YoutubePlay/PlaylistOverlay';
import AddNewTrack from './YoutubePlay/AddNewTrack';
import { recordPageVisit } from './utils/pageVisitTracker';
import { Share2 } from 'lucide-react';

// Lucide React에서 필요한 아이콘들을 가져옵니다
import { 
  SkipBack, 
  Play, 
  Pause, 
  SkipForward, 
  Repeat, 
  Shuffle, 
  Repeat1, 
  List,
  Trash2,
  ExternalLink, // 외부 링크 아이콘 추가
  MonitorIcon, // 모니터 아이콘 추가
  Headphones,
  Video,
  Film,
  Volume2,
  SmartphoneIcon, // 스마트폰 아이콘 추가
  Maximize, // 전체화면 아이콘 추가
  Minimize // 전체화면 축소 아이콘 추가
} from 'lucide-react';

// 재생 모드 상수 정의
const PLAY_MODES = {
  SEQUENTIAL: 'sequential',
  RANDOM: 'random',
  REPEAT_ONE: 'repeat_one'
};

const YoutubePlay = ({ language, user, countryCode, userIp, forceReload }) => {
  const playerRef = useRef(null);
  const containerRef = useRef(null); // 전체화면을 위한 컨테이너 참조 추가
  const [tracks, setTracks] = useState([]);
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [playMode, setPlayMode] = useState(PLAY_MODES.SEQUENTIAL);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [player, setPlayer] = useState(null);
  const [newUrl, setNewUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [albumName, setAlbumName] = useState('');
  const [userList, setUserList] = useState([]);
  const [albumList, setAlbumList] = useState([]);
  const userId = user ? user.uid : 'unknown';
  const [isPublic, setIsPublic] = useState(true); // 공개/비공개 상태를 위한 state 추가
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [manualLoadRequired, setManualLoadRequired] = useState(false);

  // localStorage에서 이전 선택값 불러오기
  const [selectedUserName, setSelectedUserName] = useState(() => {
    return localStorage.getItem('youtube_selected_user') || '';
  });
  const [selectedAlbumName, setSelectedAlbumName] = useState(() => {
    return localStorage.getItem('youtube_selected_album') || 'all';
  });
  const [debugLog, setDebugLog] = useState([]);
  const [isLoadingTitle, setIsLoadingTitle] = useState(false);
  const [playerKey, setPlayerKey] = useState(Date.now()); // 플레이어 강제 재생성을 위한 키
  
  // 레이아웃 모드 상태 (portrait = 9:16, landscape = 16:9)
  const [layoutMode, setLayoutMode] = useState(() => {
    return localStorage.getItem('youtube_layout_mode') || 'portrait';
  });
  
  const [videoNotFound, setVideoNotFound] = useState(false);

  // 전체화면 상태 추가
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  // 오디오 전용 모드 상태 추가
  const [audioOnlyMode, setAudioOnlyMode] = useState(() => {
    return localStorage.getItem('youtube_audio_only_mode') === 'true' || false;
  });

  // YouTube에서 열기 모드 상태 추가 - 기본값을 true로 설정
  const [openInYouTubeMode, setOpenInYouTubeMode] = useState(() => {
    const stored = localStorage.getItem('youtube_open_in_youtube_mode');
    // localStorage에 값이 없으면 true(체크됨)를 기본값으로 사용
    return stored !== null ? stored === 'true' : true;
  });

  // 현재 트랙의 고화질 썸네일 URL 저장
  const [highResThumbnail, setHighResThumbnail] = useState('');

  // 모바일 디바이스 감지
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [showAddTrackForm, setShowAddTrackForm] = useState(false);

  const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';
  const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

  const [visitRecorded, setVisitRecorded] = useState(false);

  // 전체화면 감지 함수
  const checkFullScreen = () => {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
  };

  useEffect(() => {
    // 이미 방문 기록이 생성된 경우 중복 실행 방지
    if (visitRecorded) return;

    // 페이지 방문 기록 남기기
    console.log(`Recording page visit with countryCode: ${countryCode}`);
    recordPageVisit('YoutubePlay', {
      userId,
      userEmail: user ? user.email : 'unknown',
      userIp,
      countryCode,
      SERVER_ROOT,
      language,
      isMobileDevice
    });
    
    // 방문 기록 완료 표시
    setVisitRecorded(true);
  }, []);

  // 전체화면 상태 변경 이벤트 리스너
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(checkFullScreen());
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullScreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullScreenChange);
    };
  }, []);
 
  // 공유된 비디오 ID 처리 (URL 파라미터 및 로컬 스토리지)  
  useEffect(() => {
    // URL 쿼리 파라미터 분석
    const urlParams = new URLSearchParams(window.location.search);
    const isDirect = urlParams.get('direct') === '1';
    const directTrackId = urlParams.get('trackId');
    const directVideoId = urlParams.get('videoId');
    
    // 직접 로드 요청이 아니면 리턴
    if (!isDirect) return;
    
    console.log('URL 파라미터 확인:', { directTrackId, directVideoId });
    
    // 트랙 목록이 로드될 때까지 대기
    if (!tracks || tracks.length === 0) {
      console.log('트랙 목록 로드 대기 중...');
      return;
    }
    
    // 1. trackId로 트랙 찾기
    if (directTrackId) {
      // 트랙 ID에 해당하는 인덱스 찾기
      const trackIndex = tracks.findIndex(track => track.id.toString() === directTrackId);
      
      if (trackIndex !== -1) {
        const track = tracks[trackIndex];
        console.log(`트랙 ID ${directTrackId}에 해당하는 트랙 찾음:`, track.videoTitle);
        
        // 사용자 필터 설정
        setSelectedUserName(track.userName);
        localStorage.setItem('youtube_selected_user', track.userName);
        
        // 앨범 필터는 'all'로 설정
        setSelectedAlbumName('all');
        localStorage.setItem('youtube_selected_album', 'all');
        
        // 필터링 후 트랙 인덱스 다시 찾기
        setTimeout(() => {
          // 필터링된 트랙 목록 내에서 해당 트랙의 인덱스 찾기
          const filteredIndex = filteredTracks.findIndex(t => t.id.toString() === directTrackId);
          
          if (filteredIndex !== -1) {
            console.log(`필터링된 목록에서 인덱스 설정: ${filteredIndex}`);
            setCurrentTrackIndex(filteredIndex);
            setIsPlaying(true);
            setManualLoadRequired(false);
          } else {
            console.log('필터링된 목록에서 트랙을 찾을 수 없음');
            // 전체 트랙 인덱스로 대체
            setCurrentTrackIndex(trackIndex);
            setIsPlaying(true);
            setManualLoadRequired(false);
          }
          
          // URL에서 파라미터 제거 (깔끔한 URL 유지)
          window.history.replaceState({}, document.title, '/YoutubePlay');
        }, 800); // 필터 적용을 위한 충분한 시간 확보
        
        return;
      } else {
        console.log(`트랙 ID ${directTrackId}에 해당하는 트랙을 찾을 수 없음`);
      }
    }
    
    // 2. videoId로 트랙 찾기
    if (directVideoId) {
      const videoIndex = tracks.findIndex(track => {
        const videoId = extractYouTubeId(track.url);
        return videoId === directVideoId;
      });
      
      if (videoIndex !== -1) {
        const track = tracks[videoIndex];
        console.log(`비디오 ID ${directVideoId}에 해당하는 트랙 찾음:`, track.videoTitle);
        
        // 사용자 필터 설정
        setSelectedUserName(track.userName);
        localStorage.setItem('youtube_selected_user', track.userName);
        
        // 앨범 필터는 'all'로 설정
        setSelectedAlbumName('all');
        localStorage.setItem('youtube_selected_album', 'all');
        
        // 필터링 후 트랙 인덱스 다시 찾기
        setTimeout(() => {
          // 필터링된 트랙 목록 내에서 해당 비디오 ID를 가진 트랙의 인덱스 찾기
          const filteredIndex = filteredTracks.findIndex(t => {
            const videoId = extractYouTubeId(t.url);
            return videoId === directVideoId;
          });
          
          if (filteredIndex !== -1) {
            console.log(`필터링된 목록에서 인덱스 설정: ${filteredIndex}`);
            setCurrentTrackIndex(filteredIndex);
            setIsPlaying(true);
            setManualLoadRequired(false);
          } else {
            console.log('필터링된 목록에서 트랙을 찾을 수 없음');
            // 전체 트랙 인덱스로 대체
            setCurrentTrackIndex(videoIndex);
            setIsPlaying(true);
            setManualLoadRequired(false);
          }
          
          // URL에서 파라미터 제거
          window.history.replaceState({}, document.title, '/YoutubePlay');
        }, 800); // 필터 적용을 위한 충분한 시간 확보
        
        return;
      } else {
        console.log(`비디오 ID ${directVideoId}에 해당하는 트랙을 찾을 수 없음`);
      }
    }
    
    // URL에서 파라미터 제거
    window.history.replaceState({}, document.title, '/YoutubePlay');
  }, [tracks, filteredTracks]);

  // 전체 트랙 목록에서 공유된 트랙을 찾고 필요한 경우 필터 설정을 변경
  // useEffect(() => {
  //   if (!tracks || tracks.length === 0) return;
    
  //   // URL에서 trackId와 video 파라미터 확인
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const trackIdParam = urlParams.get('trackId');
  //   const videoParam = urlParams.get('video');
    
  //   // 로컬 스토리지에서 공유된 비디오 ID 확인
  //   const sharedVideoId = localStorage.getItem('shared_youtube_video_id');
    
  //   if (!trackIdParam && !videoParam && !sharedVideoId) return;
    
  //   // 먼저 전체 트랙 목록에서 찾기
  //   let foundTrack = null;
  //   let foundIndex = -1;
    
  //   // trackId로 찾기 시도
  //   if (trackIdParam) {
  //     foundTrack = tracks.find(track => track.id.toString() === trackIdParam);
  //   }
    
  //   // video ID로 찾기 시도
  //   if (!foundTrack && (videoParam || sharedVideoId)) {
  //     const effectiveVideoId = videoParam || sharedVideoId;
  //     foundTrack = tracks.find(track => {
  //       const videoId = extractYouTubeId(track.url);
  //       return videoId === effectiveVideoId;
  //     });
  //   }
    
  //   // 트랙을 찾았다면 필터 설정을 변경
  //   if (foundTrack) {
  //     // 1. 해당 트랙의 사용자로 필터 변경
  //     if (foundTrack.userName !== selectedUserName) {
  //       setSelectedUserName(foundTrack.userName);
  //       localStorage.setItem('youtube_selected_user', foundTrack.userName);
  //     }
      
  //     // 2. 해당 트랙의 앨범으로 필터 변경 또는 'all'로 설정
  //     const trackAlbum = foundTrack.albumName || '기본 앨범';
  //     if (selectedAlbumName !== 'all' && selectedAlbumName !== trackAlbum) {
  //       setSelectedAlbumName('all');
  //       localStorage.setItem('youtube_selected_album', 'all');
  //     }
      
  //     // 3. 수동 로드가 필요한 상태 해제
  //     setManualLoadRequired(false);
  //   }
  // }, [tracks]);

  // 마우스 움직임을 감지하여 컨트롤 표시/숨김을 관리하는 기능 추가
  useEffect(() => {
    if (!isFullScreen || !containerRef.current) return;
    
    let mouseTimeout;
    let cursorTimeout;
    const controlsVisibilityDelay = 3000; // 3초 후 컨트롤 숨김
    const cursorVisibilityDelay = 3000; // 3초 후 커서 숨김
    
    // 마우스 움직임 감지 함수
    const handleMouseMove = () => {
      // 컨테이너에 컨트롤 표시 클래스 추가
      containerRef.current.classList.add('controls-visible');
      containerRef.current.classList.add('cursor-visible');
      
      // 이전 타이머 취소
      clearTimeout(mouseTimeout);
      clearTimeout(cursorTimeout);
      
      // 마우스 움직임이 없으면 3초 후 컨트롤 숨김
      mouseTimeout = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.classList.remove('controls-visible');
        }
      }, controlsVisibilityDelay);
      
      // 마우스 움직임이 없으면 3초 후 커서 숨김
      cursorTimeout = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.classList.remove('cursor-visible');
          containerRef.current.classList.add('cursor-hidden');
        }
      }, cursorVisibilityDelay);
    };
    
    // 컨트롤에 마우스 올렸을 때 숨김 방지
    const handleControlsMouseEnter = () => {
      clearTimeout(mouseTimeout);
      clearTimeout(cursorTimeout);
      
      if (containerRef.current) {
        containerRef.current.classList.add('controls-visible');
        containerRef.current.classList.add('cursor-visible');
        containerRef.current.classList.remove('cursor-hidden');
      }
    };
    
    // 컨트롤에서 마우스 떠났을 때 다시 타이머 시작
    const handleControlsMouseLeave = () => {
      if (!containerRef.current) return;
      
      mouseTimeout = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.classList.remove('controls-visible');
        }
      }, controlsVisibilityDelay);
      
      cursorTimeout = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.classList.remove('cursor-visible');
          containerRef.current.classList.add('cursor-hidden');
        }
      }, cursorVisibilityDelay);
    };
    
    // 초기 상태로 컨트롤 표시
    if (containerRef.current) {
      containerRef.current.classList.add('controls-visible');
      containerRef.current.classList.add('cursor-visible');
      
      // 3초 후 자동 숨김
      mouseTimeout = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.classList.remove('controls-visible');
        }
      }, controlsVisibilityDelay);
      
      cursorTimeout = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.classList.remove('cursor-visible');
          containerRef.current.classList.add('cursor-hidden');
        }
      }, cursorVisibilityDelay);
    }
    
    // 이벤트 리스너 등록
    containerRef.current.addEventListener('mousemove', handleMouseMove);
    
    // 컨트롤 요소에 마우스 이벤트 추가
    const controlsElements = containerRef.current.querySelectorAll('.youtube-controls, .youtube-filter-controls');
    controlsElements.forEach(element => {
      element.addEventListener('mouseenter', handleControlsMouseEnter);
      element.addEventListener('mouseleave', handleControlsMouseLeave);
    });
    
    // 컴포넌트 언마운트 시 이벤트 리스너 정리
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
        
        const controlsElements = containerRef.current.querySelectorAll('.youtube-controls, .youtube-filter-controls');
        controlsElements.forEach(element => {
          element.removeEventListener('mouseenter', handleControlsMouseEnter);
          element.removeEventListener('mouseleave', handleControlsMouseLeave);
        });
      }
      
      clearTimeout(mouseTimeout);
      clearTimeout(cursorTimeout);
    };
  }, [isFullScreen]);

  // 전체화면 토글 함수 수정 - 컨트롤 가시성 초기화 추가
  const toggleFullScreen = () => {
    if (!containerRef.current) return;
    
    // 현재 전체화면 상태 토글
    setIsFullScreen(!isFullScreen);
    
    try {
      // 현재 상태 저장
      let currentTime = 0;
      let wasPlaying = false;
      
      if (player) {
        try {
          if (player.getCurrentTime) currentTime = player.getCurrentTime();
          if (player.getPlayerState) wasPlaying = player.getPlayerState() === 1;
        } catch (e) {
          console.error('플레이어 상태 가져오기 오류:', e);
        }
      }
      
      // 전체화면 상태를 바꾸는 대신 CSS 클래스만 변경
      if (!isFullScreen) {
        // 전체화면으로 전환
        document.body.style.overflow = 'hidden'; // 스크롤 방지
        
        // 재생성을 위한 플레이어 키 업데이트
        setPlayerKey(Date.now());
        
        // 플레이어 상태 저장
        sessionStorage.setItem('youtube_current_time', currentTime);
        sessionStorage.setItem('youtube_was_playing', wasPlaying);
        
        // CSS 클래스 추가 (setTimeout으로 비동기 실행)
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.classList.add('manual-fullscreen-mode');
            containerRef.current.classList.add('controls-visible'); // 초기에 컨트롤 표시
            containerRef.current.classList.add('cursor-visible'); // 초기에 커서 표시
          }
        }, 0);
      } else {
        // 전체화면 종료
        document.body.style.overflow = '';
        
        // CSS 클래스 제거
        if (containerRef.current) {
          containerRef.current.classList.remove('manual-fullscreen-mode');
          containerRef.current.classList.remove('controls-visible');
          containerRef.current.classList.remove('cursor-hidden');
          containerRef.current.classList.remove('cursor-visible');
        }
        
        // 재생성을 위한 플레이어 키 업데이트
        setPlayerKey(Date.now());
      }
    } catch (error) {
      console.error('전체화면 토글 오류:', error);
      addDebugLog(`전체화면 토글 오류: ${error.message}`);
    }
  };

  // 모바일 감지 
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobileDevice(isMobile);
      
      // 모바일에서는 세로 모드 권장하지만 강제하지 않음
      // 공유 링크를 통해 들어온 경우에는 레이아웃 설정을 변경하지 않음
      const urlParams = new URLSearchParams(window.location.search);
      const isSharedLink = urlParams.has('trackId') || urlParams.has('video');
      
      if (isMobile && !isSharedLink && !localStorage.getItem('youtube_layout_mode')) {
        setLayoutMode('portrait');
        localStorage.setItem('youtube_layout_mode', 'portrait');
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // 사용자나 레이아웃 모드가 변경되면 플레이어 키 업데이트 (강제 재생성)
  useEffect(() => {
    // 플레이어 키를 업데이트하여 플레이어 재생성 강제화
    setPlayerKey(Date.now());
    
    // 플레이어 인스턴스가 있으면 정리
    if (player) {
      try {
        if (typeof player.destroy === 'function') {
          player.destroy();
        }
        setPlayer(null);
      } catch (e) {
        console.error('플레이어 정리 오류:', e);
      }
    }
  }, [user, layoutMode]);
  
  // 레이아웃 모드가 변경될 때마다 localStorage에 저장
  useEffect(() => {
    localStorage.setItem('youtube_layout_mode', layoutMode);
  }, [layoutMode]);

  // 페이지 가시성 변경 이벤트 처리 - 백그라운드 재생 지원
  useEffect(() => {
    let visibilityTimeoutId = null;
    
    const handleVisibilityChange = () => {
      // 페이지가 숨겨지면 (탭이 닫히거나 백그라운드로 갔을 때)
      if (document.hidden) {
        if (isPlaying && player && audioOnlyMode) {
          addDebugLog('백그라운드 재생 모드 활성화');
          // 백그라운드 모드에서는 계속 재생
          // 필요한 경우 추가 처리를 여기에 구현
        }
      } else {
        // 페이지가 다시 보이게 되면
        if (visibilityTimeoutId) {
          clearTimeout(visibilityTimeoutId);
        }
        
        // 잠시 후 플레이어 상태 확인 및 복원 (재생 중이었다면 계속 재생)
        visibilityTimeoutId = setTimeout(() => {
          if (isPlaying && player && typeof player.getPlayerState === 'function') {
            const playerState = player.getPlayerState();
            if (playerState !== 1) { // 재생 중이 아니면 재생 시도
              try {
                player.playVideo();
              } catch (e) {
                console.error('재생 복원 오류:', e);
              }
            }
          }
        }, 500);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeoutId) {
        clearTimeout(visibilityTimeoutId);
      }
    };
  }, [isPlaying, player, audioOnlyMode]);

  // iOS 웹 오디오 정책 대응 코드 (컴포넌트 최상위에 추가)
  useEffect(() => {
    const unlockAudioOnIOS = () => {
      // iOS 기기인지 확인
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      
      if (isIOS) {
        // 사용자 상호작용 시 오디오 컨텍스트 생성 및 재생
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // 빈 버퍼 생성 및 재생 (iOS 오디오 제한 해제)
        const buffer = audioContext.createBuffer(1, 1, 22050);
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContext.destination);
        source.start(0);
        
        // 오디오 컨텍스트 재개
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
      }
    };

    // 사용자 상호작용 이벤트에 핸들러 등록
    document.addEventListener('touchstart', unlockAudioOnIOS, { once: true });
    document.addEventListener('click', unlockAudioOnIOS, { once: true });
    
    return () => {
      document.removeEventListener('touchstart', unlockAudioOnIOS);
      document.removeEventListener('click', unlockAudioOnIOS);
    };
  }, []);

  // YouTube Player API 로드
  useEffect(() => {
    const loadYouTubeAPI = () => {
      // API가 이미 로드되어 있는지 확인
      if (window.YT && window.YT.Player) {
        setIsReady(true);
        addDebugLog('YouTube IFrame API가 이미 로드되어 있습니다.');
        return;
      }
      
      // 이전 스크립트 태그 제거 (중복 방지)
      const existingScript = document.getElementById('youtube-iframe-api');
      if (existingScript) {
        existingScript.remove();
      }
      
      // YouTube IFrame API 로드
      const tag = document.createElement('script');
      tag.id = 'youtube-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      // API 준비 완료 콜백 설정
      window.onYouTubeIframeAPIReady = () => {
        setIsReady(true);
        addDebugLog('YouTube IFrame API 로드 완료');
      };
    };
    
    loadYouTubeAPI();
    
    // 정리 함수
    return () => {
      window.onYouTubeIframeAPIReady = null;
    };
  }, [playerKey, forceReload]); // playerKey가 변경될 때마다 재실행
  
  // YouTube URL이 변경될 때 비디오 정보 가져오기
  useEffect(() => {
    const fetchVideoInfo = async () => {
      // URL이 충분히 길고 YouTube URL 형식인 경우에만 처리
      if (newUrl.length < 10) return;
      
      const videoId = extractYouTubeId(newUrl);
      if (!videoId) return;
      
      setIsLoadingTitle(true);
      setError(null);
      setVideoNotFound(false); // 초기화
      
      try {
        addDebugLog(`비디오 정보 가져오기 시도: ${videoId}`);
        
        // 서버 API를 통해 비디오 정보 가져오기
        const response = await axios.get(`${SERVER_ROOT}/api/youtube/video-info/${videoId}`);
        
        if (response.data && response.data.title) {
          setVideoTitle(response.data.title);
          addDebugLog(`비디오 제목 설정: ${response.data.title}`);
        } else {
          addDebugLog('비디오 제목을 찾을 수 없습니다.');
          setVideoNotFound(true); // 비디오를 찾지 못함 상태 설정
          
          // 3초 후 메시지 자동 사라짐
          setTimeout(() => {
            setVideoNotFound(false);
          }, 3000);
        }
      } catch (error) {
        addDebugLog(`비디오 정보 가져오기 오류: ${error.message}`);
        setVideoNotFound(true); // 오류 발생 시에도 비디오를 찾지 못함 상태 설정
        
        // 3초 후 메시지 자동 사라짐
        setTimeout(() => {
            setVideoNotFound(false);
        }, 3000);
      } finally {
        setIsLoadingTitle(false);
      }
    };
    
    // URL이 변경되면 비디오 정보 가져오기
    if (newUrl) {
      fetchVideoInfo();
    }
  }, [newUrl, SERVER_ROOT]);

  // 레이아웃 모드 토글 함수
  const toggleLayoutMode = () => {
    // 모바일 장치에서는 레이아웃 변경 불가
    if (isMobileDevice) return;
    
    const newMode = layoutMode === 'portrait' ? 'landscape' : 'portrait';
    setLayoutMode(newMode);
  };

  // 고화질 썸네일 가져오기 함수 추가
  const fetchHighResThumbnail = (track) => {
    if (!track || !track.url) return;
    
    const videoId = extractYouTubeId(track.url);
    if (!videoId) return;
    
    // 먼저 트랙 객체에 저장된 썸네일이 있는지 확인
    // if (track.thumbnail) {
    //   setHighResThumbnail(track.thumbnail);
    //   return;
    // }
    
    // YouTube 썸네일 URL 형식으로 고화질 이미지 URL 생성
    // maxresdefault.jpg가 없으면 자동으로 hqdefault.jpg로 대체됨
    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    
    // 이미지 존재 여부 확인
    const img = new Image();
    img.onload = () => {
      // 이미지 로드 성공 (고화질 썸네일 존재)
      setHighResThumbnail(thumbnailUrl);
    };
    
    img.onerror = () => {
      // 고화질 썸네일이 없는 경우 hqdefault 사용
      setHighResThumbnail(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`);
    };
    
    img.src = thumbnailUrl;
  };

  // YouTube에서 열기 모드 토글 함수 추가
  const toggleOpenInYouTubeMode = () => {
    const newMode = !openInYouTubeMode;
    setOpenInYouTubeMode(newMode);
    localStorage.setItem('youtube_open_in_youtube_mode', newMode.toString());
    
    // YouTube에서 열기 모드가 활성화되고 현재 트랙이 있으면 썸네일 가져오기
    if (newMode && filteredTracks.length > 0 && currentTrackIndex < filteredTracks.length) {
      fetchHighResThumbnail(filteredTracks[currentTrackIndex]);
    }
  };

  // 오디오 전용 모드 토글 함수 추가
  const toggleAudioOnlyMode = () => {
    const newMode = !audioOnlyMode;
    setAudioOnlyMode(newMode);
    localStorage.setItem('youtube_audio_only_mode', newMode.toString());
    
    if (newMode && filteredTracks.length > 0 && currentTrackIndex < filteredTracks.length) {
      // 오디오 모드 활성화: 현재 트랙의 고화질 썸네일 가져오기
      fetchHighResThumbnail(filteredTracks[currentTrackIndex]);
    }

    // 플레이어가 있는 경우 영상 요소의 표시 여부 설정
    if (player && player.getIframe) {
      try {
        const iframe = player.getIframe();
        if (iframe) {
          if (newMode) {
            // 오디오 모드: 비디오는 1x1 픽셀로 최소화하지만 완전히 숨기지 않음 (오디오 재생 유지)
            iframe.style.width = '1px';
            iframe.style.height = '1px';
            iframe.style.opacity = '0.01'; // 완전히 투명하게 하면 일부 브라우저에서 재생이 중지될 수 있음
            iframe.style.pointerEvents = 'none';
          } else {
            // 비디오 모드: 원래 크기로 복원
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.opacity = '1';
            iframe.style.pointerEvents = 'auto';
          }
        }
      } catch (e) {
        console.error('플레이어 iframe 스타일 설정 오류:', e);
      }
    }
    
    // 플레이어 키를 업데이트하여 새 설정으로 플레이어 재생성
    // 모바일에서는 플레이어 재생성 없이 스타일만 변경
    if (!isMobileDevice) {
      setPlayerKey(Date.now());
    }
  };

  // 디버깅 로그 함수
  const addDebugLog = (message) => {
    // 프로덕션 환경에서는 최소한의 로깅만 수행
    if (process.env.NODE_ENV !== 'production') {
      console.log(message);
    }
    // 최근 5개 로그만 유지
    setDebugLog(prev => [...prev.slice(-4), { time: new Date().toISOString(), message }]);
  };

  // 서버에서 YouTube 트랙 목록 가져오기
  useEffect(() => {
    setIsLoading(true);
    axios.get(`${SERVER_ROOT}/api/youtubePlay/all`, {
      params: {
        userEmail: user ? user.email : ''
      }
    })
      .then(response => {
        if (response.data && response.data.length > 0) {
          addDebugLog(`YouTube 트랙 ${response.data.length}개 로드됨`);
          setTracks(response.data);
          
          // 사용자 목록 추출 (중복 제거)
          const users = [...new Set(response.data.map(track => track.userName))];
          setUserList(users);
          
          // 초기 선택된 사용자 설정
          // localStorage에 저장된 사용자 이름이 있고 유효한지 확인
          const savedUserName = localStorage.getItem('youtube_selected_user');
          const userExists = savedUserName && users.includes(savedUserName);
          
          if (userExists) {
            // localStorage에 저장된 사용자가 유효하면 그대로 사용
            setSelectedUserName(savedUserName);
          } else if (user && user.displayName) {
            // 저장된 값이 없거나 유효하지 않으면 로그인한 사용자 또는 첫 번째 사용자 선택
            const loggedInUserExists = users.includes(user.displayName);
            const newSelectedUser = loggedInUserExists ? user.displayName : (users.length > 0 ? users[0] : '');
            setSelectedUserName(newSelectedUser);
            localStorage.setItem('youtube_selected_user', newSelectedUser);
          } else {
            // 로그인하지 않은 경우 첫 번째 사용자 선택
            const firstUser = users.length > 0 ? users[0] : '';
            setSelectedUserName(firstUser);
            localStorage.setItem('youtube_selected_user', firstUser);
          }
        } else {
          addDebugLog('트랙이 없습니다.');
        }
        setIsLoading(false);
      })
      .catch(err => {
        addDebugLog(`트랙 목록 가져오기 오류: ${err.message}`);
        setError('YouTube 트랙을 로드할 수 없습니다');
        setIsLoading(false);
      });
  }, [user, SERVER_ROOT]);
  
  // 사용자가 변경되면 해당 사용자의 앨범 목록 필터링
  useEffect(() => {
    if (!selectedUserName || tracks.length === 0) return;
    
    // 선택된 사용자의 트랙만 필터링
    const userTracks = tracks.filter(track => track.userName === selectedUserName);
    
    // 앨범 목록 추출 (중복 제거 및 정규화)
    const albumsSet = new Set();
    userTracks.forEach(track => {
      // 앨범 이름을 정규화 (공백 제거 및 소문자 변환)
      const normalizedAlbum = (track.albumName || '기본 앨범').trim().toLowerCase();
      albumsSet.add(normalizedAlbum);
    });
    
    // Set을 배열로 변환하고 원래 형태로 복원 (첫 글자만 대문자로)
    const albums = Array.from(albumsSet).map(album => 
      album.charAt(0).toUpperCase() + album.slice(1)
    ).sort();
    
    setAlbumList(albums);
    
    // 저장된 앨범 이름이 있는지 확인
    const savedAlbumName = localStorage.getItem('youtube_selected_album');
    
    // 저장된 앨범이 현재 사용자의 앨범 목록에 있는지 확인
    const normalizedSavedAlbum = savedAlbumName ? savedAlbumName.trim().toLowerCase() : '';
    const albumExists = albums.some(album => album.trim().toLowerCase() === normalizedSavedAlbum);
    
    if (albumExists && savedAlbumName !== 'all') {
      setSelectedAlbumName(savedAlbumName);
      localStorage.setItem('youtube_selected_album', savedAlbumName);
    } else {
      // 저장된 앨범이 없거나 현재 사용자의 앨범이 아니면 'all'로 설정
      setSelectedAlbumName('all');
      localStorage.setItem('youtube_selected_album', 'all');
    }
  }, [selectedUserName, tracks]);
  
  // 선택된 사용자와 앨범에 따라 트랙 필터링
  useEffect(() => {
    if (!selectedUserName || tracks.length === 0) return;
    
    let filtered = tracks.filter(track => track.userName === selectedUserName);
    
    if (selectedAlbumName !== 'all') {
      // 앨범 이름 비교 시 정규화하여 대소문자나 공백 차이 무시
      filtered = filtered.filter(track => 
        (track.albumName || '기본 앨범').trim().toLowerCase() === 
        selectedAlbumName.trim().toLowerCase()
      );
    }
    
    setFilteredTracks(filtered);
    
    // 플레이리스트가 변경되면 첫 트랙부터 시작
    if (filtered.length > 0 && currentTrackIndex >= filtered.length) {
      setCurrentTrackIndex(0);

      // 최초 로드가 아닌 경우에만 수동 로드 필요 상태를 true로 설정
      if (!isFirstLoad) {
        setManualLoadRequired(true);
      }
    }
  }, [selectedUserName, selectedAlbumName, tracks, currentTrackIndex]);

  // 공유된 비디오 ID 처리
  useEffect(() => {
    // 공유된 비디오 ID가 있는지 확인
    const sharedVideoId = localStorage.getItem('shared_youtube_video_id');
    
    if (sharedVideoId && filteredTracks.length > 0) {
      // 공유된 비디오 ID와 일치하는 트랙 찾기
      const trackIndex = filteredTracks.findIndex(track => {
        const videoId = extractYouTubeId(track.url);
        return videoId === sharedVideoId;
      });
      
      // 일치하는 트랙이 있으면 해당 트랙으로 이동
      if (trackIndex !== -1) {
        setCurrentTrackIndex(trackIndex);
        setIsPlaying(true); // 자동 재생
        
        // 스토리지에서 공유 ID 제거 (한 번만 사용)
        localStorage.removeItem('shared_youtube_video_id');
      }
    }
  }, [filteredTracks]);

  // 앨범 이름이 변경될 때 입력 필드의 값도 업데이트
  useEffect(() => {
    // 선택된 앨범이 'all'이 아닌 경우, 해당 앨범 이름으로 설정
    // 'all'인 경우에는 빈 문자열로 설정(기본 앨범 이름은 addNewLink에서 처리)
    if (selectedAlbumName !== 'all') {
      setAlbumName(selectedAlbumName);
    } else {
      setAlbumName('');
    }
  }, [selectedAlbumName]);

  // YouTube 플레이어 초기화 useEffect 수정
  useEffect(() => {
    if (!isReady || !filteredTracks.length || playerRef.current === null) return;

    // 공유된 링크를 통해 들어온 경우에는 항상 로드 허용
    const urlParams = new URLSearchParams(window.location.search);
    const hasShareParams = urlParams.has('trackId') || urlParams.has('video');
    
    // 수동 로드가 필요한 상태면서 공유 파라미터가 없는 경우에만 리턴
    if (!isFirstLoad && manualLoadRequired && !hasShareParams) {
      console.log('수동 로드 필요 상태 - 공유 파라미터 없음, 플레이어 초기화 건너뜀');
      return;
    }

    // 디버그 정보 출력
    addDebugLog(`플레이어 초기화: Ready=${isReady}, Tracks=${filteredTracks.length}, PlayerRef=${!!playerRef.current}, FullScreen=${isFullScreen}`);
    
    // 이전에 저장된 재생 상태 가져오기
    let savedTime = 0;
    let wasPlaying = false;
    
    try {
      const storedTime = sessionStorage.getItem('youtube_current_time');
      const storedPlaying = sessionStorage.getItem('youtube_was_playing');
      
      if (storedTime !== null) {
        savedTime = parseFloat(storedTime);
        sessionStorage.removeItem('youtube_current_time');
      }
      
      if (storedPlaying !== null) {
        wasPlaying = storedPlaying === 'true';
        sessionStorage.removeItem('youtube_was_playing');
      }
    } catch (e) {
      console.error('저장된 상태 가져오기 오류:', e);
    }
    
    // 기존 플레이어 정보 가져오기
    let currentTime = savedTime;
    let shouldPlay = wasPlaying || isPlaying;
    
    if (player && !savedTime) {
      try {
        if (player.getCurrentTime) currentTime = player.getCurrentTime();
        if (player.getPlayerState) shouldPlay = player.getPlayerState() === 1 || isPlaying;
      } catch (e) {
        console.error('플레이어 상태 가져오기 오류:', e);
      }
    }
    
    // 기존 플레이어 정리
    if (player) {
      try {
        if (player.destroy) player.destroy();
      } catch (e) {
        console.error('이전 플레이어 정리 오류:', e);
      }
    }

    // 플레이어 컨테이너 클리어 및 준비
    if (playerRef.current) {
      // 모든 자식 요소 제거
      while (playerRef.current.firstChild) {
        playerRef.current.removeChild(playerRef.current.firstChild);
      }
      
      // 새 플레이어 컨테이너 생성
      const playerContainer = document.createElement('div');
      playerContainer.id = `youtube-player-${playerKey}`;
      playerContainer.style.width = '100%';
      playerContainer.style.height = '100%';
      playerContainer.style.position = 'absolute';
      playerContainer.style.top = '0';
      playerContainer.style.left = '0';
      
      playerRef.current.appendChild(playerContainer);
      
      // 현재 트랙 인덱스 유효성 확인
      const safeIndex = Math.min(currentTrackIndex, filteredTracks.length - 1);
      const videoId = extractYouTubeId(filteredTracks[safeIndex].url);
      
      if (!videoId) {
        addDebugLog(`유효하지 않은 YouTube URL: ${filteredTracks[safeIndex].url}`);
        return;
      }
       
      try {
        // 안전하게 새 플레이어 생성
        addDebugLog(`새 플레이어 생성 시도: videoId=${videoId}, currentTime=${currentTime}, shouldPlay=${shouldPlay}`);
        
        const newPlayer = new window.YT.Player(playerContainer, {
          height: '100%',
          width: '100%',
          videoId: videoId,
          playerVars: {
            'playsinline': 1,
            'rel': 0,
            'autoplay': 0, // 초기에는 자동 재생하지 않음
            'start': Math.floor(currentTime),
            'controls': 1,
            'enablejsapi': 1,
            'origin': process.env.NODE_ENV === 'production' 
              ? 'https://www.doldari.com' 
              : window.location.origin, 
            'modestbranding': 1,
            'fs': 0, // YouTube 자체 전체화면 비활성화
            'quality': audioOnlyMode ? 'small' : 'default', // 오디오 모드에서는 최저 품질 설정
            'hl': 'ko',
            'iv_load_policy': 3,
          },
          events: {
            'onReady': (event) => {
              addDebugLog('YouTube 플레이어 준비됨');
              
              // iframe 요소 참조
              const iframe = event.target.getIframe();
              
              // iframe 스타일 직접 설정
              if (iframe) {
                // 스타일 강제 적용
                iframe.style.position = 'absolute';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                
                // 오디오 전용 모드일 경우 스타일 적용
                if (audioOnlyMode) {
                  iframe.style.width = '1px';
                  iframe.style.height = '1px';
                  iframe.style.opacity = '0.01';
                  iframe.style.pointerEvents = 'none';
                }
                
                // 중요 속성에 !important 추가를 위한 인라인 스타일
                const importantStyles = audioOnlyMode 
                  ? 'position: absolute !important; top: 0 !important; left: 0 !important;'
                  : 'position: absolute !important; top: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important;';
                iframe.setAttribute('style', iframe.getAttribute('style') + '; ' + importantStyles);
              }
              
              // 플레이어 객체 생성 완료 후 이전 상태 복원
              setTimeout(() => {
                try {
                  // 이전 재생 위치로 이동
                  if (currentTime > 0) {
                    event.target.seekTo(currentTime, true);
                  }
                  
                  // 이전에 재생 중이었으면 다시 재생
                  if (shouldPlay) {
                    event.target.playVideo();
                  }
                } catch (e) {
                  console.error('플레이어 상태 복원 오류:', e);
                }
              }, 500); // 약간의 지연 후 실행
            },
            'onStateChange': onPlayerStateChange,
            'onError': onPlayerError
          }
        });
        
        setPlayer(newPlayer);
        addDebugLog('플레이어 객체 생성 완료');

        // 최초 로드 플래그를 false로 설정
        if (isFirstLoad) {
          setIsFirstLoad(false); 
        }
        // 최초 로드 이후에는 수동 로드가 필요한 상태로 설정 
        setManualLoadRequired(true);
      } catch (error) {
        addDebugLog(`플레이어 초기화 오류: ${error.message}`);
        console.error('플레이어 초기화 오류:', error);
      }
      
    }


  }, [isReady, filteredTracks, currentTrackIndex, playerKey, isFullScreen, isFirstLoad, manualLoadRequired]); // 의존성 배열에 isFullScreen 포함

  // 플레이어 준비 완료 핸들러
  const onPlayerReady = (event) => {
    addDebugLog('YouTube 플레이어 준비됨');
    if (isPlaying) {
      try {
        event.target.playVideo();
      } catch (e) {
        console.error('자동 재생 오류:', e);
      }
    }
  };

  // 플레이어 오류 핸들러 추가
  const onPlayerError = (event) => {
    // 현재 재생 중인 동영상 정보 저장
    const currentTrack = filteredTracks[currentTrackIndex];
    const currentVideoId = currentTrack ? extractYouTubeId(currentTrack.url) : null;
    
    // 다음 트랙이 있는지 확인
    const hasNextTrack = filteredTracks.length > 1;
    
    // YouTube 플레이어 오류 코드
    const errorCodes = {
      2: "요청에 유효하지 않은 매개변수 값이 포함되어 있습니다. 동영상 ID가 올바르지 않을 수 있습니다.",
      5: "요청한 콘텐츠는 HTML5 플레이어에서 재생할 수 없는 형식입니다.",
      100: "요청한 동영상을 찾을 수 없습니다. 동영상이 삭제되었거나 비공개로 표시되었을 수 있습니다.",
      101: "요청한 동영상의 소유자가 내장 플레이어에서 재생을 허용하지 않습니다.",
      150: "요청한 동영상의 소유자가 내장 플레이어에서 재생을 허용하지 않습니다."
    };
  
    // 오류 코드가 101 또는 150인 경우 (재생 허용 안 함)
    if (event.data === 101 || event.data === 150) {
      addDebugLog(`YouTube 플레이어 오류 (재생 허용 안 함): ${currentVideoId}`);
      
      if (hasNextTrack) {
        // 알림 표시 후 다음 트랙으로 자동 이동
        const errorMessage = "이 동영상은 소유자가 내장 플레이어에서 재생을 허용하지 않아 재생할 수 없습니다. 다음 동영상으로 이동합니다.";
        
        // 잠시 에러 메시지 표시 후 다음 트랙으로 이동
        setError(errorMessage);
        setTimeout(() => {
          setError(null); // 에러 메시지 초기화
          handleNext();
        }, 3000);
      } else {
        // 다음 트랙이 없는 경우
        setError("이 동영상은 소유자가 내장 플레이어에서 재생을 허용하지 않습니다. 다른 동영상을 등록해 보세요.");
        
        // 사용자가 다른 작업을 할 수 있도록 10초 후 에러 메시지 숨김
        setTimeout(() => {
          setError(null);
        }, 10000);
      }
    } else {
      // 다른 오류 처리
      const errorMessage = errorCodes[event.data] || `알 수 없는 오류가 발생했습니다 (코드: ${event.data})`;
      addDebugLog(`YouTube 플레이어 오류: ${errorMessage}`);
      
      if (hasNextTrack) {
        setError(`${errorMessage} 다음 동영상으로 이동합니다.`);
        setTimeout(() => {
          setError(null); // 에러 메시지 초기화
          handleNext();
        }, 3000);
      } else {
        setError(`${errorMessage} 다른 동영상을 등록해 보세요.`);
        // 사용자가 다른 작업을 할 수 있도록 10초 후 에러 메시지 숨김
        setTimeout(() => {
          setError(null);
        }, 10000);
      }
    }
  };

  // 플레이어 상태 변경 핸들러
  const onPlayerStateChange = (event) => {
    // 상태 코드: -1(시작되지 않음), 0(종료), 1(재생 중), 2(일시 중지), 3(버퍼링), 5(동영상 신호)
    switch (event.data) {
      case window.YT.PlayerState.ENDED:
        addDebugLog('비디오 재생 완료');
        handleTrackFinish();
        break;
      case window.YT.PlayerState.PLAYING:
        // 처음 재생 시작 시에만 로그
        if (!isPlaying) {
          addDebugLog('비디오 재생 시작');
          setIsPlaying(true);
        }
        break;
      case window.YT.PlayerState.PAUSED:
        // 처음 일시 정지 시에만 로그
        if (isPlaying) {
          addDebugLog('비디오 일시 중지');
          setIsPlaying(false);
        }
        break;
      default:
        break;
    }
  };

  // 트랙 완료 후 다음 트랙 처리
  const handleTrackFinish = () => {
    addDebugLog(`트랙 종료: 현재 재생 모드: ${playMode}`);
    
    if (!player) {
      addDebugLog('플레이어 인스턴스가 없음');
      return;
    }
    
    if (playMode === PLAY_MODES.REPEAT_ONE) {
      // 한곡 반복 모드: 같은 트랙 다시 재생
      try {
        if (player.seekTo) player.seekTo(0);
        if (player.playVideo) player.playVideo();
      } catch (e) {
        console.error('반복 재생 오류:', e);
      }
    } else if (playMode === PLAY_MODES.RANDOM) {
      // 랜덤 모드: 다음 랜덤 트랙 재생
      let randomIndex;
      if (filteredTracks.length <= 1) {
        randomIndex = 0;
      } else {
        do {
          randomIndex = Math.floor(Math.random() * filteredTracks.length);
        } while (randomIndex === currentTrackIndex && filteredTracks.length > 1);
      }
      
      setCurrentTrackIndex(randomIndex);
    } else {
      // 순차 모드: 다음 트랙 재생
      const nextIndex = (currentTrackIndex + 1) % filteredTracks.length;
      setCurrentTrackIndex(nextIndex);
    }
  };

  // 새 비디오 로드
  const loadNewVideo = (index) => {
    if (!player || typeof player.cueVideoById !== 'function' || !filteredTracks[index]) return;
    
    const videoId = extractYouTubeId(filteredTracks[index].url);
    if (!videoId) {
      addDebugLog(`유효하지 않은 YouTube URL: ${filteredTracks[index].url}`);
      return;
    }
    
    try {
      // cueVideoById로 변경
      player.cueVideoById({
        videoId: videoId,
        startSeconds: 0,
        suggestedQuality: 'default'
      });
      
      // 비디오가 로드된 후 재생
      const timer = setTimeout(() => {
        if (isPlaying && player && typeof player.playVideo === 'function') {
          try {
            player.playVideo();
          } catch (e) {
            console.error('비디오 재생 오류:', e);
          }
        }
      }, 500);
      
      return () => clearTimeout(timer);
    } catch (error) {
      console.error('비디오 로드 오류:', error);
    }
  };

  // 트랙 인덱스 변경 감지
  useEffect(() => {  
    // YouTube에서 열기 모드일 때는 플레이어 로직을 건너뛰고 썸네일만 업데이트
    if (openInYouTubeMode && filteredTracks.length > 0 && filteredTracks[currentTrackIndex]) {
      fetchHighResThumbnail(filteredTracks[currentTrackIndex]);
      return;
    }
    
    if (!player || typeof player.cueVideoById !== 'function' || filteredTracks.length === 0 || !filteredTracks[currentTrackIndex]) return;
    
    // 수동 로드가 필요한 상태면 리턴 (필터링으로 인한 자동 로드 방지)
    if (!isFirstLoad && manualLoadRequired) return;

    const videoId = extractYouTubeId(filteredTracks[currentTrackIndex].url);
    if (!videoId) return;

    // 오디오 전용 모드이면 고화질 썸네일 가져오기
    if (audioOnlyMode) {
      fetchHighResThumbnail(filteredTracks[currentTrackIndex]);
    }
    
    try {
      player.cueVideoById({
        videoId: videoId,
        startSeconds: 0,
        suggestedQuality: 'default'
      });
      
      // 약간의 딜레이 후 재생
      const timer = setTimeout(() => {
        if (isPlaying && player && typeof player.playVideo === 'function') {
          try {
            player.playVideo();
          } catch (e) {
            console.error('비디오 재생 오류:', e);
          }
        }
      }, 500);
      
      return () => clearTimeout(timer);
    } catch (error) {
      console.error('비디오 로드 오류:', error);
    }
  }, [currentTrackIndex, filteredTracks, isPlaying, player, isFirstLoad, manualLoadRequired, audioOnlyMode]);

  // YouTube에서 열기 모드일 때 트랙 변경 감지 (썸네일 업데이트용)
  useEffect(() => {
    if (openInYouTubeMode && filteredTracks.length > 0 && currentTrackIndex < filteredTracks.length) {
      fetchHighResThumbnail(filteredTracks[currentTrackIndex]);
    }
  }, [currentTrackIndex, openInYouTubeMode, filteredTracks]);

// YouTube URL에서 ID 추출
const extractYouTubeId = (url) => {
  if (!url) return null;
  
  // const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  // const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|shorts\/|&v=)([^#&?]*).*/;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|shorts\/|live\/|&v=)([^#&?]*).*/;
  // const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|shorts\/|live\/|&v=)([^#&?\s]{11}).*/;


  const match = url.match(regExp);
  
  return (match && match[2].length === 11) ? match[2] : null;
};

// 재생/일시정지 토글
const togglePlayPause = () => {
  if (!player) return;
  
  try {
    if (isPlaying) {
      if (typeof player.pauseVideo === 'function') player.pauseVideo();
    } else {
      if (typeof player.playVideo === 'function') player.playVideo();
    }
    setIsPlaying(!isPlaying);
  } catch (e) {
    console.error('재생/정지 오류:', e);
  }

  // console.log(`Recording page visit with countryCode: ${countryCode}`);
  //         recordPageVisit('YoutubePlay', {
  //           userId,
  //           userEmail: user ? user.email : 'unknown',
  //           userIp,
  //           countryCode,
  //           SERVER_ROOT,
  //           language,
  //           isMobileDevice
  //         });
};

// 이전 트랙 재생
const handlePrev = () => {
  if (filteredTracks.length === 0) return;
  
  const prevIndex = (currentTrackIndex - 1 + filteredTracks.length) % filteredTracks.length;
  setCurrentTrackIndex(prevIndex);

  // 수동 로드 설정 해제 (자동으로 이전 트랙 로드 허용)
  setManualLoadRequired(false);
  
  // 플레이어를 직접 제어하여 이전 트랙 로드 및 재생
  if (player && filteredTracks[prevIndex]) {
    const videoId = extractYouTubeId(filteredTracks[prevIndex].url);
    if (videoId) {
      try {
        player.loadVideoById({
          videoId: videoId,
          startSeconds: 0,
          suggestedQuality: 'default'
        });
        setIsPlaying(true);
      } catch (e) {
        console.error('이전 트랙 로드 오류:', e);
      }
    }
  }
};

// 다음 트랙 재생
const handleNext = () => {
  if (filteredTracks.length === 0) return;
  
  const nextIndex = (currentTrackIndex + 1) % filteredTracks.length;
  setCurrentTrackIndex(nextIndex);

  // 수동 로드 설정 해제 (자동으로 다음 트랙 로드 허용)
  setManualLoadRequired(false);
  
  // 플레이어를 직접 제어하여 다음 트랙 로드 및 재생
  if (player && filteredTracks[nextIndex]) {
    const videoId = extractYouTubeId(filteredTracks[nextIndex].url);
    if (videoId) {
      try {
        player.loadVideoById({
          videoId: videoId,
          startSeconds: 0,
          suggestedQuality: 'default'
        });
        setIsPlaying(true);
      } catch (e) {
        console.error('다음 트랙 로드 오류:', e);
      }
    }
  }
};

// 재생 모드 전환 (순차 -> 랜덤 -> 한곡 반복)
const togglePlayMode = () => {
  setPlayMode(prevMode => {
    let newMode;
    if (prevMode === PLAY_MODES.SEQUENTIAL) {
      newMode = PLAY_MODES.RANDOM;
    } else if (prevMode === PLAY_MODES.RANDOM) {
      newMode = PLAY_MODES.REPEAT_ONE;
    } else {
      newMode = PLAY_MODES.SEQUENTIAL;
    }
    addDebugLog(`재생 모드 변경: ${prevMode} -> ${newMode}`);
    return newMode;
  });
};

// 트랙 선택 
const selectTrack = (index) => {
  // 먼저 트랙 인덱스를 업데이트하여 썸네일이 변경되도록 함
  setCurrentTrackIndex(index);
  setShowPlaylist(false);
  
  // YouTube에서 열기 모드가 활성화된 경우
  if (openInYouTubeMode && filteredTracks[index]) {
    const videoId = extractYouTubeId(filteredTracks[index].url);
    if (videoId) {
      // 썸네일 업데이트를 위해 약간의 지연 후 YouTube 앱 열기
      setTimeout(() => {
        openInYouTubeApp(videoId);
      }, 100);
      return;
    }
  }
  
  // 명시적인 트랙 선택 시에만 로드되도록 설정
  setManualLoadRequired(false); 
};

// 필터된 트랙 목록이 비어있으면 "No tracks found" 로딩 상태 보여주기
useEffect(() => {
  if (filteredTracks.length === 0 && !isLoading) {
    setIsLoading(false);
    if (selectedUserName && selectedAlbumName) {
      addDebugLog(`${selectedUserName}의 ${selectedAlbumName === 'all' ? '모든 앨범' : selectedAlbumName}에 해당하는 트랙이 없습니다.`);
    }
  }
}, [filteredTracks, isLoading, selectedUserName, selectedAlbumName]);

// 플레이리스트 토글
const togglePlaylist = () => {
  // 가로 모드일 때는 플레이리스트가 항상 표시되므로 무시
  if (layoutMode === 'landscape' && !isFullScreen) return;
  
  // 세로 모드 또는 전체화면 모드일 때만 토글
  console.log('togglePlaylist');
  setShowPlaylist(!showPlaylist);
};

// 새 트랙 추가 후 콜백 함수
const handleTrackAdded = (newTrack) => {
  // 새 트랙 추가 후 목록 갱신
  setTracks(prev => [...prev, newTrack]);
  
  // 사용자 목록 업데이트 - 현재 사용자가 목록에 없으면 추가
  setUserList(prev => {
    if (!prev.includes(user.displayName)) {
      return [...prev, user.displayName];
    }
    return prev;
  });
  
  addDebugLog('새 YouTube 링크가 추가되었습니다.');
};

// 트랙 추가 폼 토글
const toggleAddTrackForm = () => {
  setShowAddTrackForm(!showAddTrackForm);
};

// 새 링크 추가 처리
const addNewLink = async () => {
  if (!newUrl || !user || !user.email) return;
  
  try {
    // YouTube ID 추출 검증
    const videoId = extractYouTubeId(newUrl);
    if (!videoId) {
      setError('유효한 YouTube URL이 아닙니다.');
      return;
    }
  
    // 비디오 제목이 비어 있을 경우
    if (!videoTitle) {
      setError('비디오 제목을 입력해주세요.');
      return;
    }
  
    // 앨범 이름 처리 - 없을 경우 현재 선택된 앨범 사용 (all이면 '기본 앨범')
    const effectiveAlbumName = albumName.trim() || 
      (selectedAlbumName !== 'all' ? selectedAlbumName : '기본 앨범');
  
    const response = await axios.post(`${SERVER_ROOT}/api/youtubePlay`, {
      url: newUrl,
      videoTitle: videoTitle,
      userEmail: user.email,
      userName: user.displayName,
      albumName: effectiveAlbumName,
      isPublic: isPublic // 공개/비공개 상태 추가
    });
  
    if (response.data) {
      // 새 트랙 추가 후 목록 갱신
      setTracks(prev => [...prev, response.data]);
      
      // 사용자 목록 업데이트 - 현재 사용자가 목록에 없으면 추가
      setUserList(prev => {
        if (!prev.includes(user.displayName)) {
          return [...prev, user.displayName];
        }
        return prev;
      });
      
      setNewUrl('');
      setVideoTitle('');
      setAlbumName('');
      addDebugLog('새 YouTube 링크가 추가되었습니다.');
    }
  } catch (err) {
    addDebugLog(`링크 추가 오류: ${err.message}`);
    setError('YouTube 링크를 추가하는 중 오류가 발생했습니다.');
  }
};

// selectTrack과 같은 함수 근처에 toggleTrackVisibility 함수 추가 
const toggleTrackVisibility = async (trackId, isPublic) => {
  try {
    await axios.put(`${SERVER_ROOT}/api/youtubePlay/toggle-public/${trackId}`, {
      userEmail: user?.email,
      isPublic: isPublic
    });
    
    // tracks 상태 업데이트
    setTracks(prev => prev.map(track => 
      track.id === trackId ? { ...track, isPublic } : track
    ));
    
    // filteredTracks도 업데이트
    setFilteredTracks(prev => prev.map(track => 
      track.id === trackId ? { ...track, isPublic } : track
    ));
    
    addDebugLog(`트랙 ID ${trackId}의 공개 상태가 ${isPublic ? '공개' : '비공개'}로 변경되었습니다.`);
  } catch (error) {
    console.error('트랙 공개 상태 변경 오류:', error);
    setError('트랙 공개 상태를 변경하는 중 오류가 발생했습니다.');
  }
};

// 트랙 삭제 처리
const deleteTrack = async (id, index, e) => {
e.stopPropagation(); // 이벤트 버블링 방지

if (!user || !user.email) return;

try {
  await axios.delete(`${SERVER_ROOT}/api/youtubePlay/${id}`, {
    params: { userEmail: user.email }
  });

  // 마스터 트랙 목록에서 제거
  const newTracks = tracks.filter(track => track.id !== id);
  setTracks(newTracks);
  
  // 필터된 트랙 목록은 상위 useEffect에서 다시 계산됨
  
  addDebugLog(`트랙이 삭제되었습니다. ID: ${id}`);
} catch (err) {
  addDebugLog(`트랙 삭제 오류: ${err.message}`);
  setError('트랙을 삭제하는 중 오류가 발생했습니다.');
}
};

// 사용자 선택 변경 처리
const handleUserChange = (e) => {
  const newUserName = e.target.value;
  setSelectedUserName(newUserName);
  // localStorage에 선택 저장
  localStorage.setItem('youtube_selected_user', newUserName);
};

// 앨범 선택 변경 처리
const handleAlbumChange = (e) => {
  const newAlbumName = e.target.value;
  setSelectedAlbumName(newAlbumName);
  // localStorage에 선택 저장
  localStorage.setItem('youtube_selected_album', newAlbumName);
 
  setShowPlaylist(true);
};

// 재생 모드에 따른 텍스트 반환
const getPlayModeText = () => {
switch (playMode) {
  case PLAY_MODES.SEQUENTIAL:
    return '순차 재생';
  case PLAY_MODES.RANDOM:
    return '랜덤 재생';
  case PLAY_MODES.REPEAT_ONE:
    return '한곡 반복';
  default:
    return '순차 재생';
}
};

// 재생 모드에 따른 아이콘 반환
const getPlayModeIcon = () => {
switch (playMode) {
  case PLAY_MODES.RANDOM:
    return <Shuffle size={18} />;
  case PLAY_MODES.REPEAT_ONE:
    return <Repeat1 size={18} />; 
  case PLAY_MODES.SEQUENTIAL:
  default:
    return <Repeat size={18} />;
}
};

// 유튜브 앱에서 열기 함수
const openInYouTubeApp = (videoId) => {
if (!videoId) return;

// 모바일 디바이스 감지
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

if (isMobile) {
  // 모바일에서는 YouTube 앱으로 직접 이동
  // Android와 iOS 모두 이 형식의 URL 스키마를 지원합니다
  window.location.href = `youtube://watch?v=${videoId}`;
  
  // 앱이 열리지 않으면 잠시 후 웹 버전으로 리다이렉트
  setTimeout(() => {
    window.location.href = `https://www.youtube.com/watch?v=${videoId}`;
  }, 2000);
} else {
  // 데스크톱에서는 새 탭에서 YouTube 웹사이트 열기
  window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
}
};

// 플레이어 객체가 변경될 때 이전 인스턴스 정리
useEffect(() => {
return () => {
  if (player) {
    try {
      if (typeof player.stopVideo === 'function') player.stopVideo();
      if (typeof player.destroy === 'function') player.destroy();
    } catch (e) {
      console.error('플레이어 정리 오류:', e);
    }
  }
};
}, [player]);

// 로딩 중이거나 에러 상태 표시
if (isLoading) {
return (
  <div className="youtube-player-container">
    <p style={{color: 'white', fontSize: '12px'}}>YouTube 동영상 로딩 중...</p>
  </div>
);
}

if (error) {
return (
  <div className="youtube-player-container">
    <p style={{color: 'red', fontSize: '12px'}}>{error}</p>
    <button onClick={() => setError(null)} className="control-button">다시 시도</button>
  </div>
);
}

// 컴포넌트 내 함수로 분리 (다른 핸들러 함수 근처에 위치)
const handleShareTrack = () => {
  // 항상 최신 currentTrackIndex 사용
  if (!filteredTracks.length || currentTrackIndex >= filteredTracks.length) return;
  
  const track = filteredTracks[currentTrackIndex];
  const videoId = extractYouTubeId(track.url);
  
  // 개발환경인지 프로덕션 환경인지 확인하여 적절한 도메인 사용
  const domain = process.env.NODE_ENV === 'production' 
    ? 'https://www.doldari.com' 
    : window.location.origin;
  
  const shareUrl = `https://www.doldari.com:3001/share/youtube?trackId=${track.id}&video=${videoId}&t=${Date.now()}`;
  console.log(`shareUrl2: ${shareUrl}`);

  navigator.clipboard.writeText(shareUrl)
    .then(() => {
      alert(language !== 'KR' ? 'Track link copied to clipboard!' : '트랙 링크가 클립보드에 복사되었습니다!');
    })
    .catch(err => {
      console.error('클립보드 복사 오류:', err);
      alert(language !== 'KR' ? 'Failed to copy link' : '링크 복사에 실패했습니다');
    });
};


return (
<div className={`youtube-player-container ${layoutMode}-mode ${isFullScreen ? 'fullscreen-mode' : ''} ${audioOnlyMode ? 'audio-only-mode' : ''}`} 
     ref={containerRef} 
     style={{ width: '95%' }}>
  {/* 레이아웃에 따라 다른 UI 구성 적용 */}  

    

    <>
      {/* 필터 컨트롤 추가 */}
      <div className="youtube-filter-controls">
        <div className="filter-control">
          <label htmlFor="user-select">{language !== 'KR' ? 'Album Owner' : '앨범 등록자'}</label>
          <select 
            id="user-select" 
            value={selectedUserName} 
            onChange={handleUserChange}
            className="youtube-select"
            style={{textAlign: 'center'}}
          >
            {/* {userList.map((userName, index) => (
              <option key={index} value={userName}>{userName}</option>
            ))} */}
            {userList.map((userName, index) => (
              <option key={index} value={userName}>
                {userName === user?.displayName 
                  ? `${userName} (${language !== 'KR' ? 'Myself' : '나'})`
                  : userName}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-control">
          <label htmlFor="album-select">{language !== 'KR' ? 'Album Name' : '앨범 이름'}</label>
          <select 
            id="album-select" 
            value={selectedAlbumName} 
            onChange={handleAlbumChange}
            className="youtube-select"
          >
            <option value="all">모든 앨범</option>
            {albumList.map((album, index) => (
              <option key={index} value={album}>{album}</option>
            ))}
          </select>
        </div> 
        
        {/* 전체화면 토글 버튼 추가 */}
        <button
          className="control-button fullscreen-toggle"
          onClick={toggleFullScreen}
          title={isFullScreen ? "전체화면 종료" : "전체화면으로 보기"}
        >
          {isFullScreen ? <Minimize size={16} /> : <Maximize size={16} />}
        </button>
      </div> 
      
      <div className="youtube-player" style={{ width: '100%', height: 'auto', padding: 0 }}>
        {/* 오디오 전용 모드일 때 썸네일 컨테이너 표시 */}
        {audioOnlyMode && !openInYouTubeMode && (
          <div className="audio-thumbnail-container">
            <div className="audio-thumbnail-wrapper">
              <img 
                src={highResThumbnail || (filteredTracks[currentTrackIndex]?.thumbnail || `${SERVER_ROOT}/images/thumbnail_default.png`)} 
                alt={filteredTracks[currentTrackIndex]?.videoTitle || "재생 중인 오디오"} 
                className="audio-thumbnail-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `${SERVER_ROOT}/images/thumbnail_default.png`;
                }}
              />
            </div>
            <div className="audio-track-info">
              <div className="audio-track-title">
                {filteredTracks[currentTrackIndex]?.videoTitle || "알 수 없는 트랙"}
              </div>
              <div className="audio-track-channel">
                {filteredTracks[currentTrackIndex]?.channelName || "알 수 없는 채널"}
              </div>
              <div className="audio-mode-indicator">
                {language === 'KR' ? '오디오 전용 모드 재생 중' : 'Audio only mode'}
              </div>
            </div>
          </div>
        )}
        
        {/* YouTube에서 열기 모드일 때 썸네일 표시 */}
        {openInYouTubeMode && filteredTracks.length > 0 && currentTrackIndex < filteredTracks.length ? (
          <div className="youtube-thumbnail-container" style={{
            width: '100%',
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#000',
            position: 'relative',
            minHeight: isMobileDevice ? '200px' : '500px'  
          }}>
            <img 
              src={(() => {
                const videoId = extractYouTubeId(filteredTracks[currentTrackIndex].url);
                return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : `${SERVER_ROOT}/images/hqdefault.jpg`;
              })()} 
              alt={filteredTracks[currentTrackIndex]?.videoTitle || "비디오 썸네일"} 
              style={{
                maxWidth: '100%',
                maxHeight: 'auto',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
              onError={(e) => {
                e.target.onerror = null;
                const videoId = extractYouTubeId(filteredTracks[currentTrackIndex].url);
                if (videoId) {
                  e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                } else {
                  e.target.src = `${SERVER_ROOT}/images/thumbnail_default.png`;
                }
              }}
            />

            {/* 커다란 PLAY 버튼 오버레이 */}
            <button 
              className="youtube-play-overlay-button"
              onClick={() => {
                const videoId = extractYouTubeId(filteredTracks[currentTrackIndex].url);
                if (videoId) {
                  openInYouTubeApp(videoId);
                }
              }}
              title={'Play on YouTube'}
            >
              <Play size={isMobileDevice ? 40 : 60} />
            </button>

            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,0.8)',
              color: 'white',
              padding: '12px 16px',
              borderRadius: '6px',
              fontSize: isMobileDevice ? '12px' : '14px',
              textAlign: 'center',
              maxWidth: '90%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}>
              <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>
                {filteredTracks[currentTrackIndex]?.videoTitle || '알 수 없는 트랙'}
              </div>
              <div style={{ fontSize: isMobileDevice ? '10px' : '12px', opacity: '0.9' }}>
                {language === 'KR' ? 'YouTube에서 열기 모드 - 트랙을 선택하면 YouTube 앱에서 재생됩니다' : 'Open in YouTube Mode - Select track to play in YouTube app'}
              </div>
            </div>
          </div>
        ) : (
          /* 기존 YouTube 플레이어 (오디오 모드에서도 유지하되 시각적으로만 숨김) */
          <div className="youtube-player" style={{ width: '100%', height: 'auto' }}>
            <div ref={playerRef} id="youtube-player-element"></div>

          </div>        
        )}

        {/* 세로 모드 또는 전체화면 모드일 때만 PlaylistOverlay 사용 */}
        
        {/* {(layoutMode === 'portrait' || isFullScreen) && ( */}
          <PlaylistOverlay
            isVisible={showPlaylist}
            onClose={togglePlaylist}
            tracks={filteredTracks}
            currentTrackIndex={currentTrackIndex}
            onSelectTrack={selectTrack}
            onDeleteTrack={deleteTrack}
            onToggleVisibility={toggleTrackVisibility} // 새로운 prop
            userEmail={user?.email}
            language={language}
            isFullScreen={isFullScreen}
            SERVER_ROOT={SERVER_ROOT}
          />
        {/* )} */}
      </div>


      {isMobileDevice ? (  
        <>
        <div className="track-info" style={{fontSize: '12px', color: 'white'}}>
          {filteredTracks.length > 0 && currentTrackIndex < filteredTracks.length && (
            <p style={{textAlign: "center"}}>{`${
              filteredTracks[currentTrackIndex].videoTitle ? 
              `${filteredTracks[currentTrackIndex].videoTitle} ` :
              (filteredTracks[currentTrackIndex].albumName || '알 수 없음')
            }`}</p>
            // <p>{`제목: ${
            //   filteredTracks[currentTrackIndex].videoTitle ? 
            //   `${filteredTracks[currentTrackIndex].videoTitle} in ${filteredTracks[currentTrackIndex].albumName || '기본 앨범'}` :
            //   (filteredTracks[currentTrackIndex].albumName || '알 수 없음')
            // }`}</p>
          )}
          {!isReady && <p style={{color: '#ffcc00'}}>YouTube 플레이어 로딩 중...</p>}
          <p className="play-mode-indicator">
            재생 모드: {getPlayModeText()}
          </p>
          <p className="filter-indicator">
            {selectedUserName}의 {selectedAlbumName === 'all' ? '모든 앨범' : selectedAlbumName} 재생 중 ({filteredTracks.length}곡)
          </p>
        </div>  
        
        {/* 새 링크 추가 폼 */}
        <div className="add-link-form">
          <h4>{language !== 'KR' ? "Register a new YouTube URL" : "Youtube URL 등록하기"}</h4>
          <input
            type="text"
            placeholder={selectedAlbumName !== 'all' ? selectedAlbumName : (language !== 'KR' ? "Select album, or Write a new album name." : "앨범을 선택하세요. 새 앨범을 생성하려면 앨범 이름을 입력하세요.")}
            value={albumName}
            onChange={(e) => setAlbumName(e.target.value)}
            className="youtube-input"
            style={{textAlign: 'center', width: '90%'}}
          />
          
          {isLoadingTitle && (
            <div className="loading-indicator" style={{textAlign: 'center', width: '90%', margin: '5px auto', fontSize: '12px', color: '#aaa'}}>
              {language !== 'KR' ? "Loading video info..." : "비디오 정보 불러오는 중..."}
            </div>
          )}
          {videoNotFound && (
            <div className="video-not-found-message" style={{textAlign: 'center', width: '90%', margin: '5px auto', fontSize: '12px', color: '#ff6b6b', padding: '5px', backgroundColor: 'rgba(255, 107, 107, 0.1)', borderRadius: '4px'}}>
              {language !== 'KR' ? "Video information not found. Please check URL or enter details manually." : "비디오 정보를 찾을 수 없습니다. URL을 확인하거나 수동으로 정보를 입력해주세요."}
            </div>
          )}
          <input
            type="text"
            placeholder={language !== 'KR' ? "Video Title" : "비디오 제목"}
            value={videoTitle}
            onChange={(e) => setVideoTitle(e.target.value)}
            className="youtube-input"
            style={{textAlign: 'center', width: '90%', marginTop: '5px'}}
          />

          <input
            type="text"
            placeholder={language !== 'KR' ? "Paste a YouTube URL." : "YouTube URL을 여기에 붙여넣기 하세요."}
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className="youtube-input"
            style={{textAlign: 'center', width: '90%'}}
          />
          
          <button onClick={addNewLink} className="add-button" style={{textAlign: 'center', width: '90%'}}>
            {language !== 'KR' ? "Submit" : "등록"}
          </button>
        </div>

        {/* 아이콘이 적용된 컨트롤 버튼 */} 
        <div className="youtube-controls">
          <button onClick={handlePrev} className="control-button" title="이전">
            <SkipBack size={18} />
          </button>
          <button onClick={togglePlayPause} className="control-button" title={isPlaying ? '일시정지' : '재생'}>
            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
          </button>
          <button onClick={handleNext} className="control-button" title="다음">
            <SkipForward size={18} />
          </button>
          <button 
            onClick={togglePlayMode} 
            className={`control-button mode-button ${
              playMode === PLAY_MODES.RANDOM 
                ? 'random-active' 
                : playMode === PLAY_MODES.REPEAT_ONE 
                  ? 'repeat-active' 
                  : ''
            }`}
            data-mode={playMode}
            title={getPlayModeText()}
          >
            {getPlayModeIcon()}
          </button>
          
          <button onClick={togglePlaylist} className="control-button playlist-button" title="목록">
            <List size={18} />
          </button>
          
          <button 
            onClick={toggleAudioOnlyMode} 
            className={`control-button audio-mode-button ${audioOnlyMode ? 'audio-active' : ''}`}
            title={audioOnlyMode ? '비디오 모드로 전환' : '오디오 전용 모드로 전환'}
          >
            {audioOnlyMode ? <Headphones size={18} /> : <Film size={18} />}
          </button>

 
          {filteredTracks.length > 0 && currentTrackIndex < filteredTracks.length && (
            // <button 
            //   onClick={() => {
            //     const videoId = extractYouTubeId(filteredTracks[currentTrackIndex].url);
            //     if (videoId) {
            //       openInYouTubeApp(videoId);
            //     }
            //   }} 
            //   className="control-button youtube-app-button" 
            //   title="YouTube 앱에서 열기"
            // >
            //   <ExternalLink size={18} />
            // </button> 

            <button 
              onClick={handleShareTrack}
              className="share-button"
              title={language !== 'KR' ? 'Share this track' : '이 트랙 공유하기'}
            >
              <Share2 size={14} />
            </button>
          )}
          
          {/* YouTube에서 열기 체크박스 추가 (모바일) */}
          <div className="youtube-mode-checkbox-mobile" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '10px',
            color: 'white',
            fontSize: '12px'
          }}>
            <input
              type="checkbox"
              id="openInYouTubeMobileModeMode"
              checked={openInYouTubeMode}
              onChange={toggleOpenInYouTubeMode}
              style={{ marginRight: '5px' }}
            />
            <label htmlFor="openInYouTubeMobileModeMode" style={{ cursor: 'pointer' }}>
              {language !== 'KR' ? 'Open in YouTube' : 'YouTube에서 열기'}
            </label>
          </div>
        </div>
        
        </>
      ) : (/* no mobile device */
        <>
        {/* <div className="track-info" style={{fontSize: '12px', color: 'white'}}>
          {filteredTracks.length > 0 && currentTrackIndex < filteredTracks.length && (
            <p style={{textAlign: "center"}}>{`${
              filteredTracks[currentTrackIndex].videoTitle ? 
              `${filteredTracks[currentTrackIndex].videoTitle} ` :
              (filteredTracks[currentTrackIndex].albumName || '알 수 없음')
            }`}</p>
            // <p>{`제목: ${
            //   filteredTracks[currentTrackIndex].videoTitle ? 
            //   `${filteredTracks[currentTrackIndex].videoTitle} in ${filteredTracks[currentTrackIndex].albumName || '기본 앨범'}` :
            //   (filteredTracks[currentTrackIndex].albumName || '알 수 없음')
            // }`}</p>
          )}
          {!isReady && <p style={{color: '#ffcc00'}}>YouTube 플레이어 로딩 중...</p>}
          <p className="play-mode-indicator">
            재생 모드: {getPlayModeText()}
          </p>
          <p className="filter-indicator">
            {selectedUserName}의 {selectedAlbumName === 'all' ? '모든 앨범' : selectedAlbumName} 재생 중 ({filteredTracks.length}곡)
          </p>
        </div>  */}
        
        {/* 아이콘이 적용된 컨트롤 버튼 */}
        <div className="youtube-controls">
        <button onClick={handlePrev} className="control-button" title="이전">
          <SkipBack size={18} />
        </button>
        <button onClick={togglePlayPause} className="control-button" title={isPlaying ? '일시정지' : '재생'}>
          {isPlaying ? <Pause size={18} /> : <Play size={18} />}
        </button>
        <button onClick={handleNext} className="control-button" title="다음">
          <SkipForward size={18} />
        </button>
        <button 
          onClick={togglePlayMode} 
          className={`control-button mode-button ${
            playMode === PLAY_MODES.RANDOM 
              ? 'random-active' 
              : playMode === PLAY_MODES.REPEAT_ONE 
                ? 'repeat-active' 
                : ''
          }`}
          data-mode={playMode}
          title={getPlayModeText()}
        >
          {getPlayModeIcon()}
        </button>
        
        <button onClick={togglePlaylist} className="control-button playlist-button" title="목록">
          <List size={18} />
        </button>

        {/* {isMobileDevice && (
        <PlaylistOverlay
          isVisible={showPlaylist}
          onClose={togglePlaylist}
          tracks={filteredTracks}
          currentTrackIndex={currentTrackIndex}
          onSelectTrack={selectTrack}
          onDeleteTrack={deleteTrack}
          onToggleVisibility={toggleTrackVisibility} // 새로운 prop
          userEmail={user?.email}
          language={language}
          isFullScreen={isFullScreen}
          SERVER_ROOT={SERVER_ROOT}
        />
      )} */}
        
        {/* 유튜브 앱에서 열기 버튼 추가 */}
        {filteredTracks.length > 0 && currentTrackIndex < filteredTracks.length && (
          <button 
            onClick={() => {
              const videoId = extractYouTubeId(filteredTracks[currentTrackIndex].url);
              if (videoId) {
                openInYouTubeApp(videoId);
              }
            }} 
            className="control-button youtube-app-button" 
            title="YouTube 앱에서 열기"
          >
            <ExternalLink size={18} />
          </button>
        )}

        <button 
          onClick={toggleAudioOnlyMode} 
          className={`control-button audio-mode-button ${audioOnlyMode ? 'audio-active' : ''}`}
          title={audioOnlyMode ? '비디오 모드로 전환' : '오디오 전용 모드로 전환'}
        >
          {audioOnlyMode ? <Headphones size={18} /> : <Film size={18} />}
        </button>

        {/* 새 링크 추가 폼 */} 
        <button onClick={toggleAddTrackForm} className="addurl-button">
          {language !== 'KR' ? "Add a new URL" : "새로운 URL 등록"}
        </button> 

        {/* YouTube에서 열기 체크박스 추가 */}
        <div className="youtube-mode-checkbox" style={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: '10px',
          color: 'white',
          fontSize: '12px'
        }}>
          <input
            type="checkbox"
            id="openInYouTubeMode"
            checked={openInYouTubeMode}
            onChange={toggleOpenInYouTubeMode}
            style={{ marginRight: '5px' }}
          />
          <label htmlFor="openInYouTubeMode" style={{ cursor: 'pointer' }}>
            {language !== 'KR' ? 'Open in YouTube' : 'YouTube에서 열기'}
          </label>
        </div>

        {filteredTracks.length > 0 && currentTrackIndex < filteredTracks.length && (

        <button 
        onClick={handleShareTrack}
        className="share-button"
        title={language !== 'KR' ? 'Share this track' : '이 트랙 공유하기'}
        >
        <Share2 size={14} />
        </button>
        )}
          
        </div>

        
        
        {/* 트랙 추가 폼 */}
        <AddNewTrack
          isVisible={showAddTrackForm}
          onClose={toggleAddTrackForm}
          onAddTrack={handleTrackAdded}
          selectedAlbumName={selectedAlbumName}
          user={user}
          language={language}
          isFullScreen={isFullScreen}
          SERVER_ROOT={SERVER_ROOT}
        />
        </>
      )} 

    </>

    
  
</div>
);
};

export default YoutubePlay;